import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Link from "@/components/feature/LocaleLink";
import PageLayout from "@/components/feature/PageLayout";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/lib/useLang";
import { localize } from "@/lib/locale";
import { DEFAULT_IMAGE, SITE_URL } from "@/lib/useSeo";
import { NEWS, findNews, type NewsItem } from "@/data/news";
import { OG_IMAGES } from "@/data/og-images";

// 관련 글: 같은 분류의 글을 날짜순으로 둥글게 이어 붙여, 이전 · 다음 글을 뺀 3편을 고릅니다.
// 최신 글만 반복해서 걸지 않고 모든 글이 고르게 서로 연결되도록 해 검색엔진이 오래된 글까지 찾아가게 합니다.
function relatedOf(item: NewsItem, cat: (n: NewsItem) => string, exclude: (NewsItem | undefined)[]) {
  const same = NEWS.filter((n) => cat(n) === cat(item));
  const pool = same.length >= 4 ? same : NEWS;
  const at = pool.findIndex((n) => n.id === item.id);
  const skip = new Set([item.id, ...exclude.filter(Boolean).map((n) => n!.id)]);
  const out: NewsItem[] = [];
  for (let k = 1; k < pool.length && out.length < 3; k++) {
    const n = pool[(at + k) % pool.length];
    if (!skip.has(n.id)) out.push(n);
  }
  return out;
}

// 본문 인라인 서식: [글자](/주소) 는 링크(사이트 내부 주소는 페이지 이동, http 주소는 새 창), **글자** 는 굵게.
// 기술자료에서 관련 솔루션 · 글로 연결하는 내부 링크가 검색 순위에도 도움이 됩니다.
const INLINE = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*/g;
const linkClass = "text-primary-600 font-semibold underline underline-offset-4 decoration-primary-300 hover:text-primary-700";

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    if (m[1]) {
      const href = m[2];
      out.push(
        href.startsWith("/") ? (
          <Link key={at} to={href} className={linkClass}>
            {m[1]}
          </Link>
        ) : (
          <a key={at} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
            {m[1]}
          </a>
        )
      );
    } else {
      out.push(
        <strong key={at} className="font-semibold text-foreground-950">
          {m[3]}
        </strong>
      );
    }
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function NoticeDetailPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { id } = useParams();
  const item = findNews(id);

  if (!item) return <NotFound />;

  const idx = NEWS.findIndex((n) => n.id === item.id);
  const prev = NEWS[idx + 1];
  const next = NEWS[idx - 1];

  // 본문 문단을 소제목("## ") · 목록("- ") · 일반 문단으로 나눠 렌더링 (연속된 목록 항목은 하나의 <ul> 로 묶음)
  const blocks: ({ type: "h2" | "p"; text: string } | { type: "ul"; items: string[] })[] = [];
  for (const line of item.body[lang]) {
    if (line.startsWith("## ")) blocks.push({ type: "h2", text: line.slice(3) });
    else if (line.startsWith("- ")) {
      const last = blocks[blocks.length - 1];
      if (last && last.type === "ul") last.items.push(line.slice(2));
      else blocks.push({ type: "ul", items: [line.slice(2)] });
    } else blocks.push({ type: "p", text: line });
  }

  // 게시일은 연 · 월 · 일이 모두 있는 글만 표기 (예: 연도만 적힌 사업소식은 생략)
  const published = /^\d{4}\.\d{2}\.\d{2}$/.test(item.date) ? item.date.replace(/\./g, "-") : undefined;
  const pageUrl = `${SITE_URL}${localize(`/notice/${item.id}`, lang === "en")}`;
  const related = relatedOf(item, (n) => t(n.catKey), [prev, next]);
  // 글 제목이 담긴 공유 이미지 (scripts/og-images.mjs 가 빌드 때 생성, 없으면 회사 공통 이미지)
  const og = OG_IMAGES[`${lang}/${item.id}`];
  const image = og ? `${SITE_URL}${og.url}` : DEFAULT_IMAGE;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t(item.titleKey),
    description: t(item.excerptKey),
    image: [image],
    ...(published ? { datePublished: published, dateModified: published } : {}),
    inLanguage: lang,
    articleSection: t(item.catKey),
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    author: { "@type": "Organization", name: "주식회사 오션커넥트", url: `${SITE_URL}/` },
    publisher: {
      "@type": "Organization",
      name: "주식회사 오션커넥트",
      "@id": `${SITE_URL}/#organization`,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-512.png`, width: 512, height: 512 },
    },
  };

  return (
    <PageLayout
      eyebrow={t(item.catKey)}
      title={t(item.titleKey)}
      desc={item.date}
      seoDescription={t(item.excerptKey)}
      seoImage={image}
      seoType="article"
      seoPublished={published}
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <article className="max-w-3xl">
        {blocks.map((b, i) =>
          b.type === "h2" ? (
            <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
              {b.text}
            </h2>
          ) : b.type === "ul" ? (
            <ul key={i} className="list-disc pl-6 space-y-2 mb-5 text-foreground-700 text-base md:text-lg leading-relaxed">
              {b.items.map((li, j) => (
                <li key={j}>{renderInline(li)}</li>
              ))}
            </ul>
          ) : (
            <p key={i} className="text-foreground-700 text-base md:text-lg leading-relaxed mb-5">
              {renderInline(b.text)}
            </p>
          )
        )}
      </article>

      {related.length > 0 && (
        <section className="max-w-3xl mt-12" aria-labelledby="related-heading">
          <h2 id="related-heading" className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
            {t("notice.related")}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {related.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/notice/${r.id}`}
                  className="group flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-4 hover:border-primary-400 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <span className="text-xs text-foreground-500 mb-1.5">{r.date}</span>
                  <span className="text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-600">{t(r.titleKey)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <nav className="max-w-3xl mt-12 pt-6 border-t border-background-200 space-y-2 text-sm">
        {next && (
          <Link to={`/notice/${next.id}`} className="flex gap-3 py-2 text-foreground-700 hover:text-primary-600 cursor-pointer">
            <span className="w-12 shrink-0 text-foreground-500">{t("notice.next")}</span>
            {t(next.titleKey)}
          </Link>
        )}
        {prev && (
          <Link to={`/notice/${prev.id}`} className="flex gap-3 py-2 text-foreground-700 hover:text-primary-600 cursor-pointer">
            <span className="w-12 shrink-0 text-foreground-500">{t("notice.prev")}</span>
            {t(prev.titleKey)}
          </Link>
        )}
        <Link
          to="/notice"
          className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 font-medium hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-list-check"></i>
          {t("notice.list")}
        </Link>
      </nav>
    </PageLayout>
  );
}
